import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { UPLOAD_URL } from "../../api/api";
import { T, RAW } from "../../components/em/ui";

/**
 * OrgCanvas — unified absolute-positioned org chart (EM design handoff).
 * Supports three layouts (vertical, horizontal, network) with computed
 * coordinates + SVG connectors + wheel-zoom + drag-pan + collapse + tooltips.
 * Wired to the real org tree (roots/orphans). Replaces the old CSS-flexbox
 * Vertical/Horizontal/Network components so all three views render correctly.
 */
const CARD_W = 200,
  CARD_H = 80;
const V_GAP = 66,
  SIB_GAP = 22;
const LEVEL_H = CARD_H + V_GAP;

const initialsOf = (name = "") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";
const resolvePhoto = (p) =>
  !p
    ? null
    : /^https?:\/\//.test(p)
      ? p
      : `${UPLOAD_URL}${p.startsWith("/") ? "" : "/"}${p}`;

// ── layout ──────────────────────────────────────────────────────────────────
function subtreeExtent(node, collapsed) {
  if (collapsed.has(node.id) || !node.children?.length) return CARD_W;
  const sum = node.children.reduce(
    (s, c) => s + subtreeExtent(c, collapsed),
    0,
  );
  return Math.max(CARD_W, sum + SIB_GAP * (node.children.length - 1));
}
function assignVertical(node, x, y, collapsed, pos) {
  pos[node.id] = { x: x - CARD_W / 2, y };
  if (collapsed.has(node.id) || !node.children?.length) return;
  const total =
    node.children.reduce((s, c) => s + subtreeExtent(c, collapsed), 0) +
    SIB_GAP * (node.children.length - 1);
  let cx = x - total / 2;
  for (const c of node.children) {
    const w = subtreeExtent(c, collapsed);
    assignVertical(c, cx + w / 2, y + LEVEL_H, collapsed, pos);
    cx += w + SIB_GAP;
  }
}
function assignHorizontal(node, x, y, collapsed, pos) {
  pos[node.id] = { x, y: y - CARD_H / 2 };
  if (collapsed.has(node.id) || !node.children?.length) return;
  const total =
    node.children.reduce((s, c) => s + subtreeExtent(c, collapsed), 0) +
    SIB_GAP * (node.children.length - 1);
  let cy = y - total / 2;
  for (const c of node.children) {
    const w = subtreeExtent(c, collapsed);
    assignHorizontal(c, x + LEVEL_H, cy + w / 2, collapsed, pos);
    cy += w + SIB_GAP;
  }
}
function assignNetwork(node, pos, level = 0, idx = 0, total = 1) {
  const angle = (idx / total) * Math.PI * 2;
  const r = level === 0 ? 0 : level * 300;
  pos[node.id] = {
    x: 600 + r * Math.cos(angle) - CARD_W / 2,
    y: 420 + r * Math.sin(angle) - CARD_H / 2,
  };
  if (node.children?.length)
    node.children.forEach((c, i) =>
      assignNetwork(c, pos, level + 1, i, node.children.length),
    );
}
function collectEdges(node, edges, collapsed) {
  if (!node.children?.length || collapsed.has(node.id)) return edges;
  for (const c of node.children) {
    edges.push({ from: node.id, to: c.id });
    collectEdges(c, edges, collapsed);
  }
  return edges;
}
function collectAll(node, acc) {
  acc.push(node);
  node.children?.forEach((c) => collectAll(c, acc));
  return acc;
}

const SYNTH = "__synthetic_root__";

const colorFor = (n) => {
  if (!n.primary_manager_id) return RAW.orange; // root / CEO
  if (n.children && n.children.length) return RAW.info; // manager
  return RAW.fg5; // employee
};

// ── node card ────────────────────────────────────────────────────────────────
function NodeCard({
  node,
  collapsed,
  onCollapse,
  highlighted,
  onPath,
  dropTarget,
  onPick,
  onTooltip,
  employeesById,
}) {
  const color = colorFor(node);
  const childCount = node.children?.length || 0;
  const direct = node.directReports != null ? node.directReports : childCount;
  const total = node.totalReports != null ? node.totalReports : null;
  const isCollapsed = collapsed.has(node.id);
  const photo = resolvePhoto(node.profile_photo);
  const dottedName =
    node.dotted_manager_id && employeesById
      ? employeesById.get(node.dotted_manager_id)?.name
      : null;
  return (
    <div
      onMouseEnter={(e) => onTooltip(node, e)}
      onMouseLeave={() => onTooltip(null, null)}
      onClick={() => onPick?.(node)}
      style={{
        width: CARD_W,
        height: CARD_H,
        boxSizing: "border-box",
        background: "var(--em-bg-card)",
        border: `1px solid ${dropTarget ? "#16a34a" : highlighted || onPath ? T.orange : "var(--em-border)"}`,
        borderRadius: 12,
        borderTop: `3px solid ${color}`,
        boxShadow: dropTarget
          ? "0 0 0 3px rgba(22,163,74,0.35)"
          : highlighted
          ? `0 0 0 3px ${T.orangeSoft}`
          : onPath
          ? `0 0 0 2px ${T.orangeSoft}`
          : "0 2px 8px -4px rgba(10,10,10,0.12)",
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "0 12px",
        cursor: onPick ? "pointer" : "default",
        userSelect: "none",
        position: "relative",
      }}
    >
      {total > 0 && (
        <span
          title={`${direct} direct report${direct === 1 ? "" : "s"} · ${total} total`}
          style={{
            position: "absolute",
            bottom: -9,
            right: 10,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "1px 7px",
            borderRadius: 9999,
            background: "var(--em-bg-card)",
            border: `1px solid ${color}55`,
            font: `700 9px ${T.font}`,
            color,
            whiteSpace: "nowrap",
          }}
        >
          👥 {direct} · {total}
        </span>
      )}
      {photo ? (
        <img
          src={photo}
          alt={node.name}
          style={{
            width: 38,
            height: 38,
            borderRadius: 9999,
            objectFit: "cover",
            flexShrink: 0,
            border: `2px solid ${color}40`,
          }}
        />
      ) : (
        <div
          style={{
            width: 38,
            height: 38,
            borderRadius: 9999,
            background: `${color}20`,
            color,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            font: `700 13px ${T.font}`,
            flexShrink: 0,
            border: `2px solid ${color}40`,
          }}
        >
          {initialsOf(node.name)}
        </div>
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: `600 12px/1.3 ${T.font}`,
            color: T.fg1,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {node.name}
        </div>
        {node.designation && (
          <div
            style={{
              font: `400 10px/1.4 ${T.font}`,
              color: T.fg5,
              marginTop: 1,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {node.designation}
          </div>
        )}
        {node.department && (
          <div
            style={{
              font: `500 9px ${T.font}`,
              color,
              marginTop: 2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {node.department}
          </div>
        )}
        {dottedName && (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 3,
              marginTop: 2,
              padding: "1px 5px",
              borderRadius: 4,
              background: "rgba(142,81,255,0.08)",
              border: "1px solid rgba(142,81,255,0.2)",
            }}
          >
            <span style={{ font: `500 8px ${T.font}`, color: T.purple500 }}>
              ↗ dotted: {dottedName.split(" ")[0]}
            </span>
          </div>
        )}
      </div>
      {childCount > 0 && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onCollapse(node.id);
          }}
          style={{
            width: 20,
            height: 20,
            borderRadius: 9999,
            border: `1px solid ${color}40`,
            background: isCollapsed ? `${color}18` : "#fff",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            font: `700 10px ${T.font}`,
            color,
          }}
        >
          {isCollapsed ? `+${childCount}` : "–"}
        </button>
      )}
    </div>
  );
}

// ── canvas ───────────────────────────────────────────────────────────────────
const OrgCanvas = forwardRef(function OrgCanvas(
  {
    roots = [],
    orphans = [],
    mode = "vertical",
    highlightId,
    pathIds,
    onPick,
    onZoomChange,
    onReorg,
    canReorg = false,
    employeesById,
  },
  ref,
) {
  const [collapsed, setCollapsed] = useState(new Set());
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 40, y: 40 });
  const [tooltip, setTooltip] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });
  const [dragId, setDragId] = useState(null);   // node being dragged (reorg)
  const [dropId, setDropId] = useState(null);   // node hovered as drop target
  const isPanning = useRef(false);
  const lastPan = useRef({ x: 0, y: 0 });
  const [, force] = useState(0);
  const wrapRef = useRef(null);    // outer scroll/pan container (for centering)
  const contentRef = useRef(null); // transformed content div (capture target)
  const geomRef = useRef({ positions: {}, canvasW: 0, canvasH: 0 });

  const pathSet = pathIds instanceof Set ? pathIds : new Set(pathIds || []);

  useImperativeHandle(ref, () => ({
    zoomIn: () =>
      setZoom((z) => {
        const n = Math.min(2, +(z + 0.1).toFixed(2));
        onZoomChange?.(Math.round(n * 100));
        return n;
      }),
    zoomOut: () =>
      setZoom((z) => {
        const n = Math.max(0.3, +(z - 0.1).toFixed(2));
        onZoomChange?.(Math.round(n * 100));
        return n;
      }),
    resetZoom: () => {
      setZoom(1);
      setPan({ x: 40, y: 40 });
      onZoomChange?.(100);
    },
    // Expand any collapsed ancestors (given the id path), then centre the node
    // in the viewport at the current zoom (runs after the expand re-render).
    focusNode: (id, ancestorIds = []) => {
      setCollapsed((s) => {
        if (!ancestorIds.length) return s;
        const n = new Set(s);
        ancestorIds.forEach((a) => n.delete(a));
        return n;
      });
      setTimeout(() => {
        const g = geomRef.current;
        const p = g.positions[id];
        const box = wrapRef.current?.getBoundingClientRect();
        if (!p || !box) return;
        setZoom((z) => {
          setPan({
            x: box.width / 2 - (p.x + CARD_W / 2) * z,
            y: box.height / 3 - (p.y + CARD_H / 2) * z,
          });
          return z;
        });
        force((n) => n + 1);
      }, 60);
    },
    // Capture the whole chart (reset to 1:1 first so nothing is clipped),
    // then restore the viewport. Returns a PNG data URL.
    exportImage: async () => {
      const { toPng } = await import("html-to-image");
      const g = geomRef.current;
      const prevZoom = zoom, prevPan = pan;
      setZoom(1); setPan({ x: 0, y: 0 });
      await new Promise((r) => setTimeout(r, 120));
      let url = null;
      try {
        url = await toPng(contentRef.current, {
          backgroundColor: "#ffffff",
          pixelRatio: 2,
          width: g.canvasW,
          height: g.canvasH,
        });
      } finally {
        setZoom(prevZoom); setPan(prevPan);
      }
      return url;
    },
  }));

  const forest = [...(roots || []), ...(orphans || [])];
  if (!forest.length) {
    return (
      <div
        style={{
          height: "100%",
          minHeight: 420,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          border: `1px solid ${T.border}`,
          borderRadius: 14,
          background: T.bgHead,
        }}
      >
        <strong style={{ font: `600 15px ${T.font}`, color: T.fg1 }}>
          No employees yet
        </strong>
        <span style={{ font: `400 13px ${T.font}`, color: T.fg5 }}>
          Add employees and assign managers to see the chart.
        </span>
      </div>
    );
  }

  const synth = { id: SYNTH, children: forest };
  const positions = {};
  if (mode === "vertical") assignVertical(synth, 0, 0, collapsed, positions);
  else if (mode === "horizontal")
    assignHorizontal(synth, 0, 0, collapsed, positions);
  else assignNetwork(synth, positions);

  const allNodes = collectAll(synth, []).filter((n) => n.id !== SYNTH);
  const edges = collectEdges(synth, [], collapsed).filter(
    (e) => e.from !== SYNTH,
  );

  // Normalise so content starts near the top-left (synth root sits off-grid).
  const xs = allNodes.map((n) => positions[n.id]?.x ?? 0);
  const ys = allNodes.map((n) => positions[n.id]?.y ?? 0);
  const minX = Math.min(...xs, 0),
    minY = Math.min(...ys, 0);
  allNodes.forEach((n) => {
    const p = positions[n.id];
    if (p) {
      p.x -= minX;
      p.y -= minY;
    }
  });
  const canvasW = Math.max(
    800,
    Math.max(...allNodes.map((n) => positions[n.id].x)) + CARD_W + 80,
  );
  const canvasH = Math.max(
    500,
    Math.max(...allNodes.map((n) => positions[n.id].y)) + CARD_H + 80,
  );

  // Expose latest geometry to the imperative handle (focusNode / exportImage).
  geomRef.current = { positions, canvasW, canvasH };

  const toggleCollapse = (id) =>
    setCollapsed((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const onWheel = (e) => {
    e.preventDefault();
    const d = e.deltaY > 0 ? 0.92 : 1.08;
    setZoom((z) => {
      const n = Math.min(2, Math.max(0.3, +(z * d).toFixed(2)));
      onZoomChange?.(Math.round(n * 100));
      return n;
    });
  };
  const onMouseDown = (e) => {
    if (e.target.closest("[data-node]") || e.target.closest("button")) return;
    isPanning.current = true;
    lastPan.current = { x: e.clientX - pan.x, y: e.clientY - pan.y };
  };
  const onMouseMove = (e) => {
    if (!isPanning.current) return;
    setPan({
      x: e.clientX - lastPan.current.x,
      y: e.clientY - lastPan.current.y,
    });
    force((n) => n + 1);
  };
  const onMouseUp = () => {
    isPanning.current = false;
  };

  return (
    <div
      style={{
        position: "relative",
        height: "100%",
        minHeight: 420,
        borderRadius: 14,
        overflow: "hidden",
        border: `1px solid ${T.border}`,
        background: mode === "network" ? "#fafafa" : T.bgHead,
        boxShadow: T.shadowSm,
      }}
    >
      {mode === "network" && (
        <svg
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
          }}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <pattern
              id="orgGrid"
              width="24"
              height="24"
              patternUnits="userSpaceOnUse"
            >
              <path
                d="M 24 0 L 0 0 0 24"
                fill="none"
                stroke="#e5e7eb"
                strokeWidth="0.5"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#orgGrid)" />
        </svg>
      )}
      <div
        ref={wrapRef}
        style={{
          position: "absolute",
          inset: 0,
          cursor: isPanning.current ? "grabbing" : "grab",
        }}
        onWheel={onWheel}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
      >
        <div
          ref={contentRef}
          style={{
            transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
            transformOrigin: "0 0",
            position: "absolute",
            width: canvasW,
            height: canvasH,
          }}
        >
          <svg
            width={canvasW}
            height={canvasH}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              pointerEvents: "none",
              overflow: "visible",
            }}
          >
            {edges.map((edge, i) => {
              const fp = positions[edge.from],
                tp = positions[edge.to];
              if (!fp || !tp) return null;
              let d;
              if (mode === "vertical") {
                const fx = fp.x + CARD_W / 2,
                  fy = fp.y + CARD_H,
                  tx = tp.x + CARD_W / 2,
                  ty = tp.y,
                  my = (fy + ty) / 2;
                d = `M${fx},${fy} C${fx},${my} ${tx},${my} ${tx},${ty}`;
              } else if (mode === "horizontal") {
                const fx = fp.x + CARD_W,
                  fy = fp.y + CARD_H / 2,
                  tx = tp.x,
                  ty = tp.y + CARD_H / 2,
                  mx = (fx + tx) / 2;
                d = `M${fx},${fy} C${mx},${fy} ${mx},${ty} ${tx},${ty}`;
              } else {
                const fx = fp.x + CARD_W / 2,
                  fy = fp.y + CARD_H / 2,
                  tx = tp.x + CARD_W / 2,
                  ty = tp.y + CARD_H / 2;
                d = `M${fx},${fy} L${tx},${ty}`;
              }
              const onPath = pathSet.has(edge.from) && pathSet.has(edge.to);
              return (
                <path
                  key={i}
                  d={d}
                  fill="none"
                  stroke={onPath ? T.orange : "#c4c4cf"}
                  strokeWidth={onPath ? 2.5 : 1.5}
                />
              );
            })}
          </svg>
          {allNodes.map((n) => {
            const pos = positions[n.id];
            if (!pos) return null;
            return (
              <div
                key={n.id}
                data-node={n.id}
                style={{ position: "absolute", left: pos.x, top: pos.y }}
                draggable={canReorg}
                onDragStart={canReorg ? (e) => { setDragId(n.id); e.dataTransfer.effectAllowed = "move"; } : undefined}
                onDragEnd={canReorg ? () => { setDragId(null); setDropId(null); } : undefined}
                onDragOver={canReorg ? (e) => { if (dragId && dragId !== n.id) { e.preventDefault(); if (dropId !== n.id) setDropId(n.id); } } : undefined}
                onDragLeave={canReorg ? () => setDropId((d) => (d === n.id ? null : d)) : undefined}
                onDrop={canReorg ? (e) => {
                  e.preventDefault();
                  if (dragId && dragId !== n.id) onReorg?.(dragId, n.id);
                  setDragId(null); setDropId(null);
                } : undefined}
              >
                <NodeCard
                  node={n}
                  collapsed={collapsed}
                  onCollapse={toggleCollapse}
                  highlighted={highlightId === n.id}
                  onPath={pathSet.has(n.id)}
                  dropTarget={dropId === n.id}
                  onPick={onPick}
                  employeesById={employeesById}
                  onTooltip={(nd, e) => {
                    setTooltip(nd);
                    if (e) setTooltipPos({ x: e.clientX, y: e.clientY });
                  }}
                />
              </div>
            );
          })}
        </div>
      </div>
      {tooltip && (
        <div
          style={{
            position: "fixed",
            left: tooltipPos.x + 12,
            top: tooltipPos.y - 54,
            zIndex: 200,
            pointerEvents: "none",
            background: T.bgBlack,
            color: "#fff",
            borderRadius: 10,
            padding: "10px 14px",
            boxShadow: "0 8px 24px -8px rgba(0,0,0,0.4)",
            minWidth: 170,
          }}
        >
          <div style={{ font: `600 13px ${T.font}` }}>{tooltip.name}</div>
          {tooltip.designation && (
            <div
              style={{
                font: `400 11px ${T.font}`,
                color: "rgba(255,255,255,0.65)",
                marginTop: 2,
              }}
            >
              {tooltip.designation}
            </div>
          )}
          {tooltip.department && (
            <div
              style={{
                font: `400 11px ${T.font}`,
                color: "rgba(255,255,255,0.5)",
                marginTop: 4,
              }}
            >
              {tooltip.department}
            </div>
          )}
          {tooltip.children?.length > 0 && (
            <div
              style={{
                font: `500 10px ${T.font}`,
                color: "rgba(255,255,255,0.4)",
                marginTop: 4,
              }}
            >
              {tooltip.children.length} direct report
              {tooltip.children.length !== 1 ? "s" : ""}
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default OrgCanvas;
